import { call, delay, put } from 'typed-redux-saga';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  invoke,
  setupSlice,
} from '@owl-frontend/redux';
import {
  fetchNotifications as fetchNotificationsApi,
  setStatus,
} from '../../api/notifications/client';
import type { Notification } from '../../api/notifications/interface';
import { globalBus } from '../../context/event-bus';

export interface NotificationsState {
  notifications: Notification[];
  count: number;
  actions: {
    fetchNotifications: AsyncActionState<{
      notifications: Notification[];
      count: number;
    }>;
    setNotificationStatus: AsyncActionState<void>;
    pollNotifications: AsyncActionState<void>;
  };
}

export const initialState: NotificationsState = {
  notifications: [],
  count: 0,
  actions: {
    fetchNotifications: { status: 'uninitialized' },
    setNotificationStatus: { status: 'uninitialized' },
    pollNotifications: { status: 'uninitialized' },
  },
};

const POLL_INTERVAL_MS = 1 * 60_000; // 1 minute
function* pollNotifications(action) {
  while (true) {
    const response = yield* call(fetchNotificationsApi);
    yield put({
      type: 'notifications/fetchNotifications/fulfilled',
      payload: response,
      // forward action.meta
      meta: action.meta,
    });
    yield delay(POLL_INTERVAL_MS);
  }
}

export enum NOTIFICATION {
  TemplateReady = 'template_ready',
  TemplateError = 'template_error',
  SummariesAndInsightsReady = 'insights_ready',
  SummariesAndInsightsError = 'insights_error',
  TimelineUpdated = 'timeline_updated',
  EventQueued = 'event_queued',
  EventProcessing = 'event_processing',
  EventProcessed = 'event_processed',
  EventProcessingFailed = 'event_processing_failed',
  EventCancelled = 'event_cancelled',
  ChatMessageCreated = 'chat_message_created',
  ChatMessageStarted = 'chat_message_started',
  ChatChunk = 'chat_chunk',
  ChatMessageComplete = 'chat_message_complete',
  ChatProposalStatusChanged = 'chat_proposal_status_changed',
  ChatSchema = 'chat_schema',
  ChatPatch = 'chat_patch',
}

export enum NOTIFICATION_EMIT_EVENTS {
  InvalidateTemplates = 'invalidate_templates',
  TemplateGenerationError = 'template_generation_error',
  SummariesAndInsightsReady = 'summaries_and_insights_ready',
  SummariesAndInsightsError = 'summaries_and_insights_error',
  TimelineUpdated = 'timeline_updated',
  EventQueued = 'event_queued',
  EventProcessing = 'event_processing',
  EventProcessed = 'event_processed',
  EventProcessingFailed = 'event_processing_failed',
  EventCancelled = 'event_cancelled',
  ChatMessageCreated = 'chat_message_created',
  ChatMessageStarted = 'chat_message_started',
  ChatChunk = 'chat_chunk',
  ChatMessageComplete = 'chat_message_complete',
  ChatProposalStatusChanged = 'chat_proposal_status_changed',
  ChatSchema = 'chat_schema',
  ChatPatch = 'chat_patch',
  Default = 'general',
}

export type SummariesNotificationData = {
  dossier_id: string;
  email: string;
  summaries_insights_status: string;
};

export type NotificationEvents =
  | {
      notification_type: NOTIFICATION.TemplateReady;
      dossier_id: string;
    }
  | {
      notification_type: NOTIFICATION.TemplateError;
      dossier_id: string;
    }
  | {
      notification_type: NOTIFICATION.SummariesAndInsightsReady;
      dossier_id: string;
      email: string;
      summaries_insights_status: string;
    }
  | {
      notification_type: NOTIFICATION.SummariesAndInsightsError;
      dossier_id: string;
      email: string;
      summaries_insights_status: string;
    }
  | {
      notification_type: NOTIFICATION.TimelineUpdated;
      dossier_id: string;
    }
  | {
      notification_type: NOTIFICATION.EventQueued;
      dossier_id: string;
      event_type: string;
      metadata?: Record<string, unknown>;
    }
  | {
      notification_type: NOTIFICATION.EventProcessing;
      dossier_id: string;
      event_type: string;
      metadata?: Record<string, unknown>;
    }
  | {
      notification_type: NOTIFICATION.EventProcessed;
      dossier_id: string;
      event_type: string;
      metadata?: Record<string, unknown>;
    }
  | {
      notification_type: NOTIFICATION.EventProcessingFailed;
      dossier_id: string;
      event_type: string;
      metadata?: Record<string, unknown>;
    }
  | {
      notification_type: NOTIFICATION.EventCancelled;
      dossier_id: string;
      event_type: string;
      metadata?: Record<string, unknown>;
    }
  | {
      notification_type: NOTIFICATION.ChatMessageCreated;
      dossier_id: string;
      thread_id: string;
      message_id: string;
      role: string;
    }
  | {
      notification_type: NOTIFICATION.ChatMessageStarted;
      dossier_id: string;
      thread_id: string;
      role: string;
    }
  | {
      notification_type: NOTIFICATION.ChatChunk;
      dossier_id: string;
      thread_id: string;
      chunk_text: string;
      seq: number;
    }
  | {
      notification_type: NOTIFICATION.ChatMessageComplete;
      dossier_id: string;
      thread_id: string;
      message_id: string;
    }
  | {
      notification_type: NOTIFICATION.ChatSchema;
      dossier_id: string;
      thread_id: string;
      data_schema: Record<string, unknown>;
      ui_schema: Record<string, unknown>;
    }
  | {
      notification_type: NOTIFICATION.ChatPatch;
      dossier_id: string;
      thread_id: string;
      data: Record<string, unknown>;
      data_schema: Record<string, unknown>;
      ui_schema: Record<string, unknown>;
      seq: number;
    }
  | {
      notification_type: NOTIFICATION.ChatProposalStatusChanged;
      dossier_id: string;
      thread_id: string;
      message_id: string;
      proposal_id: string;
      status: string;
      result: string | null;
      error: string | null;
    }
  | { notification_type?: never };

export type NotificationEmitEvents = {
  [NOTIFICATION_EMIT_EVENTS.InvalidateTemplates]: string;
  [NOTIFICATION_EMIT_EVENTS.TemplateGenerationError]: string;
  [NOTIFICATION_EMIT_EVENTS.SummariesAndInsightsReady]: SummariesNotificationData;
  [NOTIFICATION_EMIT_EVENTS.SummariesAndInsightsError]: SummariesNotificationData;
  [NOTIFICATION_EMIT_EVENTS.TimelineUpdated]: string;
  [NOTIFICATION_EMIT_EVENTS.EventQueued]: {
    dossierId: string;
    eventType: string;
    metadata: Record<string, unknown>;
  };
  [NOTIFICATION_EMIT_EVENTS.EventProcessing]: {
    dossierId: string;
    eventType: string;
    metadata: Record<string, unknown>;
  };
  [NOTIFICATION_EMIT_EVENTS.EventProcessed]: {
    dossierId: string;
    eventType: string;
    metadata: Record<string, unknown>;
  };
  [NOTIFICATION_EMIT_EVENTS.EventProcessingFailed]: {
    dossierId: string;
    eventType: string;
    metadata: Record<string, unknown>;
  };
  [NOTIFICATION_EMIT_EVENTS.EventCancelled]: {
    dossierId: string;
    eventType: string;
    metadata: Record<string, unknown>;
  };
  [NOTIFICATION_EMIT_EVENTS.ChatMessageCreated]: {
    dossierId: string;
    threadId: string;
    messageId: string;
    role: string;
  };
  [NOTIFICATION_EMIT_EVENTS.ChatMessageStarted]: {
    dossierId: string;
    threadId: string;
    role: string;
  };
  [NOTIFICATION_EMIT_EVENTS.ChatChunk]: {
    dossierId: string;
    threadId: string;
    chunkText: string;
    seq: number;
  };
  [NOTIFICATION_EMIT_EVENTS.ChatMessageComplete]: {
    dossierId: string;
    threadId: string;
    messageId: string;
  };
  [NOTIFICATION_EMIT_EVENTS.ChatSchema]: {
    dossierId: string;
    threadId: string;
    dataSchema: Record<string, unknown>;
    uiSchema: Record<string, unknown>;
  };
  [NOTIFICATION_EMIT_EVENTS.ChatPatch]: {
    dossierId: string;
    threadId: string;
    data: Record<string, unknown>;
    dataSchema: Record<string, unknown>;
    uiSchema: Record<string, unknown>;
    seq: number;
  };
  [NOTIFICATION_EMIT_EVENTS.ChatProposalStatusChanged]: {
    dossierId: string;
    threadId: string;
    messageId: string;
    proposalId: string;
    status: string;
    result: string | null;
    error: string | null;
  };
  [NOTIFICATION_EMIT_EVENTS.Default]: unknown;
};

export const notificationGlobalBusHandler = (data: NotificationEvents) => {
  if (data.notification_type === NOTIFICATION.TemplateReady) {
    globalBus.emit(
      NOTIFICATION_EMIT_EVENTS.InvalidateTemplates,
      data.dossier_id
    );
    return;
  }

  if (data.notification_type === NOTIFICATION.TemplateError) {
    globalBus.emit(
      NOTIFICATION_EMIT_EVENTS.TemplateGenerationError,
      data.dossier_id
    );
    return;
  }

  if (data.notification_type === NOTIFICATION.SummariesAndInsightsReady) {
    if (data.email && data.summaries_insights_status) {
      globalBus.emit(NOTIFICATION_EMIT_EVENTS.SummariesAndInsightsReady, {
        dossier_id: data.dossier_id,
        email: data.email,
        summaries_insights_status: data.summaries_insights_status,
      });
    }
    return;
  }

  if (data.notification_type === NOTIFICATION.SummariesAndInsightsError) {
    if (data.email && data.summaries_insights_status) {
      globalBus.emit(NOTIFICATION_EMIT_EVENTS.SummariesAndInsightsError, {
        dossier_id: data.dossier_id,
        email: data.email,
        summaries_insights_status: data.summaries_insights_status,
      });
    }
    return;
  }

  if (data.notification_type === NOTIFICATION.TimelineUpdated) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.TimelineUpdated, data.dossier_id);
    return;
  }

  if (data.notification_type === NOTIFICATION.EventQueued) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.EventQueued, {
      dossierId: data.dossier_id,
      eventType: data.event_type,
      metadata: data.metadata ?? {},
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.EventProcessing) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.EventProcessing, {
      dossierId: data.dossier_id,
      eventType: data.event_type,
      metadata: data.metadata ?? {},
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.EventProcessed) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.EventProcessed, {
      dossierId: data.dossier_id,
      eventType: data.event_type,
      metadata: data.metadata ?? {},
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.EventProcessingFailed) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.EventProcessingFailed, {
      dossierId: data.dossier_id,
      eventType: data.event_type,
      metadata: data.metadata ?? {},
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.EventCancelled) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.EventCancelled, {
      dossierId: data.dossier_id,
      eventType: data.event_type,
      metadata: data.metadata ?? {},
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.ChatMessageCreated) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.ChatMessageCreated, {
      dossierId: data.dossier_id,
      threadId: data.thread_id,
      messageId: data.message_id,
      role: data.role,
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.ChatMessageStarted) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.ChatMessageStarted, {
      dossierId: data.dossier_id,
      threadId: data.thread_id,
      role: data.role,
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.ChatChunk) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.ChatChunk, {
      dossierId: data.dossier_id,
      threadId: data.thread_id,
      chunkText: data.chunk_text,
      seq: data.seq,
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.ChatMessageComplete) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.ChatMessageComplete, {
      dossierId: data.dossier_id,
      threadId: data.thread_id,
      messageId: data.message_id,
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.ChatSchema) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.ChatSchema, {
      dossierId: data.dossier_id,
      threadId: data.thread_id,
      dataSchema: data.data_schema,
      uiSchema: data.ui_schema,
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.ChatPatch) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.ChatPatch, {
      dossierId: data.dossier_id,
      threadId: data.thread_id,
      data: data.data,
      dataSchema: data.data_schema,
      uiSchema: data.ui_schema,
      seq: data.seq,
    });
    return;
  }

  if (data.notification_type === NOTIFICATION.ChatProposalStatusChanged) {
    globalBus.emit(NOTIFICATION_EMIT_EVENTS.ChatProposalStatusChanged, {
      dossierId: data.dossier_id,
      threadId: data.thread_id,
      messageId: data.message_id,
      proposalId: data.proposal_id,
      status: data.status,
      result: data.result,
      error: data.error,
    });
    return;
  }
  return;
};

const slice = setupSlice('notifications', initialState)
  .addAsyncSagas({
    fetchNotifications: invoke(fetchNotificationsApi),
    setNotificationStatus: invoke(setStatus),
  })
  .addAsyncSagas(
    {
      pollNotifications,
    },
    {
      timeoutLimit: -1,
    }
  )
  .addReducers({
    'fetchNotifications/fulfilled': (state, action) => {
      state.notifications = action.payload.notifications;
      state.count = action.payload.count;
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
